import { BrandliveEvent, Session, SessionTypesEnum } from "types/working-model";

export class CourseChangeInvalidError extends Error {
	constructor(
		public message: string,
		public description: string
	) {
		super(message);
		this.name = "TimeChangeError";
	}
}

export class PastEventError extends Error {
	constructor(
		message: string
	) {
		super(message);
		this.name = "PastEventError";
	}
}

export const validateSessionTimeChange = (
	currentSession: Session,
	draftSession: Session,
	event: BrandliveEvent,
	publishedUrl: string | null,
) => {
	if (event.is_template) {
		return;
	}

	const currentTimestamp = currentSession.timestamp;
	const newTimestamp = draftSession.timestamp;
	const currentEndTimestamp = currentSession.end_timestamp;
	const newEndTimestamp = draftSession.end_timestamp;
	const hasStartTimeChanged = currentTimestamp !== newTimestamp;
	const now = Date.now();

	if (hasStartTimeChanged && newTimestamp && newTimestamp - now <= 0) {
		throw new PastEventError('Session must start in future');
	}

	// not a course, nothing below applies
	if (!event.settings.course_enabled) {
		return;
	}

	// regardless of whether published or not, only broadcasts are allowed
	if (draftSession.session_type !== SessionTypesEnum.broadcast) {
		throw new CourseChangeInvalidError(
			'Cannot change from broadcast session',
			'A course must be used with a broadcast session'
		);
	}

	// timestamps must always be set
	if (!currentTimestamp || !newTimestamp || !currentEndTimestamp || !newEndTimestamp) {
		throw new CourseChangeInvalidError(
			'Session time not set',
			'Session time must be set for both start and end'
		);
	}

	// event never published, should be able to fiddle with session timestamps
	if (!publishedUrl) {
		return;
	}

	const sessionHasStarted = now > currentTimestamp || now > newTimestamp;
	const sessionHasEnded = now > currentEndTimestamp || now > newEndTimestamp;
	const hasDurationChanged = currentEndTimestamp !== newEndTimestamp;
	const hasTimeFrameChanged = hasStartTimeChanged || hasDurationChanged;

	// no time change, everything ok
	if (!hasTimeFrameChanged) {
		return;
	}

	// time changed but session ended already
	if (sessionHasEnded) {
		throw new CourseChangeInvalidError(
			'Cannot change session time',
			'Updating session time not permitted for courses that are already ended.',
		);
	}

	// time changd but session started already
	if (sessionHasStarted) {
		throw new CourseChangeInvalidError(
			'Cannot change session time',
			'Updating session time not permitted for courses that are currently live.',
		);
	}
};